import { Injectable } from '@angular/core';
import axios from 'axios';
import { ContractModel } from 'src/app/global/contract-filter-model';
import { FiltersService } from './filters.service';
import { HttpClientService } from './http-client.service';
import { PaginationOptions, SpProductOrLocationExportPayload } from 'src/app/features/solution-participation-details/types/solution-participation-types';
import { BehaviorSubject } from 'rxjs';
import { UserService } from './user.service';
import { AdvancedOneCustomerFilters } from 'src/app/config/GlobalCacheLoader.service';
import { CurrencyUomService } from './currency-uom.service';
import { FilterSet } from 'src/app/global/filter-model';

@Injectable({
  providedIn: 'root',
})
export class SolutionParticipationRequests {
  private ENDPOINT_BASE_PATH: string = '/participation';
  private filterRequest: ContractModel | undefined;

  private solutionGroupsLocationDataCache = new BehaviorSubject<{[key: string]: any}>({});
  public solutionGroupsLocationData$ = this.solutionGroupsLocationDataCache.asObservable();

  private spProductViewDataCache = new BehaviorSubject<{[key: string]: any}>({});
  public spProductViewData$ = this.spProductViewDataCache.asObservable();

  private spLocationViewDataCache = new BehaviorSubject<{[key: string]: any}>({});
  public spLocationtViewData$ = this.spLocationViewDataCache.asObservable();
  private filterSet: FilterSet | undefined;

  // Add solution groups location pagination state
  private currentPaginationState = new BehaviorSubject<PaginationOptions>({
    limit: 25,
    page: 1,
    sortBy: "",
    sortOrder: "",
    tabSelected: "compliance"
  });
  public paginationState$ = this.currentPaginationState.asObservable();

  // OneCustomer observables
  private oneCustomerName: string = '';
  private advancedOneCustomerFilters : AdvancedOneCustomerFilters | undefined;

  constructor(
    private httpClientService: HttpClientService,
    private filtersService: FiltersService,
    private userService: UserService,
    private currencyUomService: CurrencyUomService
  ) {
    this.filtersService.selectedFiltersObservable.subscribe(
      (f) => (this.filterRequest = f)
    );
    this.filtersService.oneCustomerNameObservable.subscribe(f => this.oneCustomerName = f);
    this.filtersService.oneCustomerFiltersObservable.subscribe(f => this.advancedOneCustomerFilters = f);
    this.filtersService.filterSetObservable.subscribe(f => this.filterSet = f);
  }

  getPaginationState(): PaginationOptions {
    return this.currentPaginationState.value;
  }

  updatePaginationState(options: Partial<PaginationOptions>) {
    this.currentPaginationState.next({
      ...this.currentPaginationState.value,
      ...options
    });
  }

  async getSolutionParticipationSummary(advFilters: {
    [id: string]: string[];
  }) {
    try {
      if (this.filterRequest == undefined)
        throw new Error('filters are undefined');

      const payload = {
        contractModel: {
          topLevelContractGroup: this.filterRequest.topLevelContractGroup,
          secondLevelContractFilter:
            this.filterRequest.secondLevelContractFilter,
          accts: this.filterRequest.accts,
          dataView: this.filterRequest.dataView,
          division: this.filterRequest.division,
          oneCustomerName: this.oneCustomerName,
          oneCustomerFilters: this.advancedOneCustomerFilters,
          excludeDeletedAccounts: this.filterRequest.excludeDeletedAccounts
        },
        summaryAdvancedFilter: advFilters,
      };

      let url = `${this.ENDPOINT_BASE_PATH}/summary?region=${this.filterRequest?.regions}&countries=${this.filterRequest?.countries}&period=${this.filterRequest?.period}`;
      url = this.filtersService.appendCustomDateRangeToUrl(url);

      return await this.httpClientService.post(
        url,
        payload
      );
    } catch (error) {
      console.error('Error fetching data:', error);
      return axios('fail');
    }
  }

  async getSolutionParticipationCases(advFilters: { [id: string]: string[] }) {
    try {
      if (this.filterRequest == undefined)
        throw new Error('filters are undefined');

      const payload = {
        contractModel: {
          topLevelContractGroup: this.filterRequest.topLevelContractGroup,
          secondLevelContractFilter:
            this.filterRequest.secondLevelContractFilter,
          accts: this.filterRequest.accts,
          dataView: this.filterRequest.dataView,
          division: this.filterRequest.division,
          oneCustomerName: this.oneCustomerName,
          oneCustomerFilters: this.advancedOneCustomerFilters,
          excludeDeletedAccounts: this.filterRequest.excludeDeletedAccounts
        },
        summaryAdvancedFilter: advFilters,
      };

      let url = `${this.ENDPOINT_BASE_PATH}/cases?region=${this.filterRequest?.regions}&countries=${this.filterRequest?.countries}&period=${this.filterRequest?.period}`;
      url = this.filtersService.appendCustomDateRangeToUrl(url);

      return await this.httpClientService.post(
        url,
        payload
      );
    } catch (error) {
      console.error('Error fetching data:', error);
      return axios('fail');
    }
  }

  async getSolutionParticipationSales(advFilters: { [id: string]: string[] }) {
    try {
      if (this.filterRequest == undefined) {
        throw new Error('filterData is undefined');
      }

      const data = {
        contractModel: {
          topLevelContractGroup: this.filterRequest.topLevelContractGroup,
          secondLevelContractFilter:
            this.filterRequest.secondLevelContractFilter,
          accts: this.filterRequest.accts,
          dataView: this.filterRequest.dataView,
          division: this.filterRequest.division,
          oneCustomerName: this.oneCustomerName,
          oneCustomerFilters: this.advancedOneCustomerFilters,
          excludeDeletedAccounts: this.filterRequest.excludeDeletedAccounts,
          currencyCode: this.currencyUomService.getCurrency(),
          unitOfMeasure: this.currencyUomService.getUom()
        },
        summaryAdvancedFilter: advFilters,
      };

      let url = `${this.ENDPOINT_BASE_PATH}/sales?region=${this.filterRequest?.regions}&countries=${this.filterRequest?.countries}&period=${this.filterRequest?.period}`;
      url = this.filtersService.appendCustomDateRangeToUrl(url);

      return await this.httpClientService.post(
        url,
        data
      );
    } catch (error) {
      console.error('Error fetching data:', error);
      return axios('fail');
    }
  }

  async getGroupByLocation(
    solution: string | undefined,
    advFilters: { [id: string]: string[], },
    paginationOptions: PaginationOptions,
    locationId?:string,
    isLocationDrillIn:boolean=false
  ) {
    try {
      if (this.filterRequest == undefined) {
        throw new Error('filterData is undefined');
      }

    // Create a cache key from pagination params
    const userName = this.userService.getUsername() ?? "";
    const currencyCode = this.currencyUomService.getCurrency();
    const cacheKey = `user_${userName}_currencyCode${currencyCode}_${paginationOptions.page}_limit_${paginationOptions.limit}_sort_${paginationOptions.sortBy}_${paginationOptions.sortOrder}_searchTerm_${paginationOptions.searchTerm}_isLocationDrillIn_${isLocationDrillIn}_locationCode_${locationId}
                    _advFilters_${JSON.stringify(advFilters)}`;

    //Check cache first and return data if key is present
    const cachedData = this.spLocationViewDataCache.value[cacheKey];
    if (cachedData) {
      return cachedData;
    }

      const data = {
        contractModel: {
          topLevelContractGroup: this.filterRequest?.topLevelContractGroup,
          secondLevelContractFilter:this.filterRequest?.secondLevelContractFilter,
          accts: this.filterRequest.accts,
          dataView: this.filterRequest.dataView,
          division: this.filterRequest.division,
          oneCustomerName: this.oneCustomerName,
          oneCustomerFilters: this.advancedOneCustomerFilters,
          excludeDeletedAccounts: this.filterRequest.excludeDeletedAccounts,
          currencyCode: currencyCode,
          unitOfMeasure: this.currencyUomService.getUom()
        },
        locationAdvancedFilter: advFilters,
        isLocationDrillIn: isLocationDrillIn,
        drillInLocationId: locationId,
      };
      const encodedSearchTerm = encodeURIComponent(paginationOptions?.searchTerm || '');

      let url = `${this.ENDPOINT_BASE_PATH}/location?region=${this.filterRequest?.regions}&countries=${this.filterRequest?.countries}&period=${this.filterRequest?.period}&solution=${solution}&page=${paginationOptions.page}&limit=${paginationOptions.limit}&sortBy=${paginationOptions.sortBy}&sortOrder=${paginationOptions.sortOrder}&searchTerm=${encodedSearchTerm}`;
      url = this.filtersService.appendCustomDateRangeToUrl(url);

      const response = await this.httpClientService.post(
        url,
        data
      );
      const currentCache = this.spLocationViewDataCache.value;
        this.spLocationViewDataCache.next({
          ...currentCache,
          [cacheKey]: response
        });
        return response;
    } catch (error) {
      console.error('Error fetching data:', error);
      return axios('fail');
    }
  }

  async getGroupByProducts(
    solution: string | undefined,
    advProductFilters: { [id: string]: string[] },
    paginationOptions: PaginationOptions,
    isProductDrillIn?:boolean,
    productCode?:string
  ) {
    try {
      if (this.filterRequest == undefined)
        throw new Error('filters are undefined');

    // Create a cache key from pagination params
    const userName = this.userService.getUsername() ?? "";
    const currencyCode = this.currencyUomService.getCurrency();
    const cacheKey = `user_${userName}_currencyCode_${currencyCode}_${paginationOptions.page}_limit_${paginationOptions.limit}_sort_${paginationOptions.sortBy}_${paginationOptions.sortOrder}_searchTerm_${paginationOptions.searchTerm}_isProductDrillIn_${isProductDrillIn}_productCode_${productCode}
                    _advProductFilters_${JSON.stringify(advProductFilters)}`;

    //Check cache first and return data if key is present
    const cachedData = this.spProductViewDataCache.value[cacheKey];
    if (cachedData) {
      return cachedData;
    }
      const payload = {
        contractModel: {
          topLevelContractGroup: this.filterRequest.topLevelContractGroup,
          secondLevelContractFilter:
            this.filterRequest.secondLevelContractFilter,
          accts: this.filterRequest.accts,
          dataView: this.filterRequest.dataView,
          division: this.filterRequest.division,
          oneCustomerName: this.oneCustomerName,
          oneCustomerFilters: this.advancedOneCustomerFilters,
          excludeDeletedAccounts: this.filterRequest.excludeDeletedAccounts,
          currencyCode: this.currencyUomService.getCurrency(),
          unitOfMeasure: this.currencyUomService.getUom()
        },
        productAdvancedFilter: advProductFilters,
        isProductDrillIn: isProductDrillIn,
        drillInProductCode:productCode,
      };

      const encodedSearchTerm = encodeURIComponent(paginationOptions?.searchTerm || '');

      let url = `${this.ENDPOINT_BASE_PATH}/product?region=${this.filterRequest?.regions}&countries=${this.filterRequest?.countries}&period=${this.filterRequest?.period}&solution=${solution}&page=${paginationOptions.page}&limit=${paginationOptions.limit}&sortBy=${paginationOptions.sortBy}&sortOrder=${paginationOptions.sortOrder}&searchTerm=${encodedSearchTerm}`;
      url = this.filtersService.appendCustomDateRangeToUrl(url);

      const response = await this.httpClientService.post(
        url,
        payload
      );

      const currentCache = this.spProductViewDataCache.value;
        this.spProductViewDataCache.next({
          ...currentCache,
          [cacheKey]: response
        });
        return response;
    } catch (error) {
      console.error('Error fetching data:', error);
      return axios('fail');
    }
  }

  async getSolutionParticipationSummaryFilters() {
    try {
      if (this.filterRequest == undefined) {
        throw new Error('filterData is undefined');
      }

      const payload = {
        topLevelContractGroup: this.filterRequest.topLevelContractGroup,
        secondLevelContractFilter: this.filterRequest.secondLevelContractFilter,
        accts: this.filterRequest.accts,
        dataView: this.filterRequest.dataView,
        division: this.filterRequest.division,
        excludeDeletedAccounts: this.filterRequest.excludeDeletedAccounts
      };

      let url = `${this.ENDPOINT_BASE_PATH}/summary/filters?region=${this.filterRequest?.regions}&countries=${this.filterRequest?.countries}&period=${this.filterRequest?.period}`;
      url = this.filtersService.appendCustomDateRangeToUrl(url);

      return await this.httpClientService.post(
        url,
        payload
      );
    } catch (error) {
      console.error('Error fetching data:', error);
      return axios('fail');
    }
  }

  async getSolutionSummaryDetailsAdvFilters() {
    try {
      if (this.filterRequest == undefined) {
        throw new Error('filterData is undefined');
      }

      const payload = {
        topLevelContractGroup: this.filterRequest.topLevelContractGroup,
        secondLevelContractFilter: this.filterRequest.secondLevelContractFilter,
        accts: this.filterRequest.accts,
        dataView: this.filterRequest.dataView,
        division: this.filterRequest.division,
        oneCustomerName: this.oneCustomerName,
        oneCustomerFilters: this.advancedOneCustomerFilters,
        excludeDeletedAccounts: this.filterRequest.excludeDeletedAccounts
      };

      let url = `${this.ENDPOINT_BASE_PATH}/summary-details/filters?region=${this.filterRequest?.regions}&countries=${this.filterRequest?.countries}&period=${this.filterRequest?.period}`;
      url = this.filtersService.appendCustomDateRangeToUrl(url);

      return await this.httpClientService.post(
        url,
        payload
      );
    } catch (error) {
      console.error('Error fetching data:', error);
      return axios('fail');
    }
  }

  //#region Solution Participation v3
  async getSolutionGroupsByLocation(
    widgetType: string,
    paginationOptions: PaginationOptions,
    advProductFilters?: { [id: string]: string[] },
    useCache: boolean = true) {

    // Update pagination state
    this.updatePaginationState(paginationOptions);

    // Create a cache key from pagination params
    const userName = this.userService.getUsername() ?? "";
    const currencyCode = this.currencyUomService.getCurrency();
    const cacheKey = `user_${userName}_${widgetType}currencyCode${currencyCode}_${paginationOptions.page}_limit_${paginationOptions.limit}_sort_${paginationOptions.sortBy}_${paginationOptions.sortOrder}_advProductFilters_${JSON.stringify(advProductFilters)}`;
    //Check cache first and return data if key is present
    const cachedData = this.solutionGroupsLocationDataCache.value[cacheKey];
    if (cachedData) {
      return cachedData;
    }

    try {
      if (this.filterRequest == undefined) {
        throw new Error('filterData is undefined');
      }

      const payload = {
        contractModel: {
          topLevelContractGroup: this.filterRequest.topLevelContractGroup,
          secondLevelContractFilter:
            this.filterRequest.secondLevelContractFilter,
          accts: this.filterRequest.accts,
          dataView: this.filterRequest.dataView,
          division: this.filterRequest.division,
          oneCustomerName: this.oneCustomerName,
          oneCustomerFilters: this.advancedOneCustomerFilters,
          excludeDeletedAccounts: this.filterRequest.excludeDeletedAccounts,
          currencyCode: currencyCode,
          unitOfMeasure: this.currencyUomService.getUom()
        },
        locationAdvancedFilter: advProductFilters,
      };

      let url = `${this.ENDPOINT_BASE_PATH}/solutiongroups/${widgetType}?region=${this.filterRequest?.regions}&countries=${this.filterRequest?.countries}&period=${this.filterRequest?.period}&limit=${paginationOptions?.limit}&page=${paginationOptions?.page}&sortBy=${paginationOptions?.sortBy}&sortOrder=${paginationOptions?.sortOrder}&tabSelected=${paginationOptions?.tabSelected}`;
      url = this.filtersService.appendCustomDateRangeToUrl(url);

      const response = this.httpClientService.post(
        url,
        payload
      );

      //Update cache with new data only useCache is true
      if(useCache){
        const currentCache = this.solutionGroupsLocationDataCache.value;
        this.solutionGroupsLocationDataCache.next({
          ...currentCache,
          [cacheKey]: response
        });
      }
      return response;
    } catch (error) {
      console.error('Error fetching data for Solution Groups By Location API:', error);
      return axios('fail');
    }
  }

  /**
   * Downloads Solution Groups data as Excel file
   * @param widgetType - Type of widget (rebate/mpl/spl/all)
   * @param paginationOptions - Current pagination and sorting options
   * @param advProductFilters - Advanced filter options
   * @returns Promise with Excel file blob
  */
  async exportSolutionGroupsData( widgetType: string, paginationOptions: PaginationOptions, advProductFilters?: { [id: string]: string[] }) {
    try {
      if (this.filterRequest == undefined) {
        throw new Error('filterData is undefined');
      }

      const payload = {
        contractModel: {
          topLevelContractGroup: this.filterRequest.topLevelContractGroup,
          secondLevelContractFilter: this.filterRequest.secondLevelContractFilter,
          accts: this.filterRequest.accts,
          dataView: this.filterRequest.dataView,
          division: this.filterRequest.division,
          oneCustomerName: this.oneCustomerName,
          oneCustomerFilters: this.advancedOneCustomerFilters,
          excludeDeletedAccounts: this.filterRequest.excludeDeletedAccounts,
          currencyCode: this.currencyUomService.getCurrency(),
          unitOfMeasure: this.currencyUomService.getUom()
        },
        locationAdvancedFilter: advProductFilters,
      };

      // Build URL with sorting parameters(sortyBy and sortOrder required) to maintain UI sort order in export
      let url = `${this.ENDPOINT_BASE_PATH}/solutiongroups/${widgetType}/export?region=${this.filterRequest?.regions}&countries=${this.filterRequest?.countries}&period=${this.filterRequest?.period}&sortBy=${paginationOptions?.sortBy}&sortOrder=${paginationOptions?.sortOrder}`;
      url = this.filtersService.appendCustomDateRangeToUrl(url);

      return await this.httpClientService.post(
        url,
        payload,
        'blob' // blob response type is used for file download
      );
    } catch (error) {
      console.error('Error downloading Solution Groups Excel:', error);
      return axios('fail');
    }
  }

  async downloadExcel(payload:SpProductOrLocationExportPayload){
    try {
      if (this.filterRequest == undefined) {
        throw new Error('filterData is undefined');
      }
      let url = `${this.ENDPOINT_BASE_PATH}/export?region=${this.filterRequest?.regions}&countries=${this.filterRequest?.countries}&period=${this.filterRequest?.period}`;
      url = this.filtersService.appendCustomDateRangeToUrl(url);

      // Now assign values 
      payload.contractModel!.currencyCode = this.currencyUomService.getCurrency(); 
      payload.contractModel!.unitOfMeasure = this.currencyUomService.getUom();
      payload.oneCustomerName = this.oneCustomerName;
      payload.oneCustomerFilters = this.advancedOneCustomerFilters;
      return await this.httpClientService.post(
        url,
        payload,
        'blob'
      );
    } catch (error) {
      console.error('Error downloading Excel:', error);
      return axios('fail');
    }
  }

  async validateAccountHierarchy(){
    try{
      if (this.filterSet == undefined) {
        throw new Error('filterData is undefined');
      }
      const payload = {
        ...this.filterSet,
        CurrencyCode: this.currencyUomService.getCurrency(),
        UnitOfMeasure: this.currencyUomService.getUom(),
      };
      let url = `${this.ENDPOINT_BASE_PATH}/accounts/validate`;
      return await this.httpClientService.post(
        url,
        payload);
    }catch(error){
      console.error('Error validating account hierarchy:', error);
      return axios('fail');
    }
  }

  async checkForMultipleRebateAgreements(){
    try{
      if (this.filterSet == undefined) {
        throw new Error('filterData is undefined');
      }
      const payload = {
        ...this.filterSet,
        CurrencyCode: this.currencyUomService.getCurrency(),
        UnitOfMeasure: this.currencyUomService.getUom(),
      };
      let url = `${this.ENDPOINT_BASE_PATH}/solutiongroups/check-multiple-agreements`;
      return await this.httpClientService.post(
        url,
        payload);
    }catch(error){
      console.error('Error from solutiongroups/check-multiple-agreements API:', error);
      return axios('fail');
    }
  }

  clearCache() {
    this.solutionGroupsLocationDataCache.next({});
  }

  clearProductViewCache() {
    this.spProductViewDataCache.next({});
  }

  clearLocationViewCache() {
    this.spLocationViewDataCache.next({});
  }

  async getSPRebateWidgetData(widgetType: string, advProductFilters?: { [id: string]: string[] }) {
    try {
      if (this.filterRequest == undefined) {
        throw new Error('filterData is undefined');
      }

      const payload = {
        contractModel : {
          topLevelContractGroup: this.filterRequest.topLevelContractGroup,
          secondLevelContractFilter: this.filterRequest.secondLevelContractFilter,
          accts: this.filterRequest.accts,
          dataView: this.filterRequest.dataView,
          division: this.filterRequest.division,
          excludeDeletedAccounts: this.filterRequest.excludeDeletedAccounts
        },
        locationAdvancedFilter: advProductFilters,
      };

      let url = `${this.ENDPOINT_BASE_PATH}/rebate-widget?region=${this.filterRequest?.regions}&countries=${this.filterRequest?.countries}&period=${this.filterRequest?.period}&widgetType=${widgetType}`;
      url = this.filtersService.appendCustomDateRangeToUrl(url);

      return await this.httpClientService.post(
        url,
        payload
      );
    } catch (error) {
      console.error('Error fetching data for SP Rebate Widget Data API:', error);
      return axios('fail');
    }
  }

  async getSPProductWidgetData(widgetType: string, advProductFilters?: { [id: string]: string[] }) {
    try {
      if (this.filterRequest == undefined) {
        throw new Error('filterData is undefined');
      }

      const payload = {
        contractModel : {
          topLevelContractGroup: this.filterRequest.topLevelContractGroup,
          secondLevelContractFilter: this.filterRequest.secondLevelContractFilter,
          accts: this.filterRequest.accts,
          dataView: this.filterRequest.dataView,
          division: this.filterRequest.division,
          excludeDeletedAccounts: this.filterRequest.excludeDeletedAccounts
        },
        locationAdvancedFilter: advProductFilters,
      };

      let url = `${this.ENDPOINT_BASE_PATH}/productlist-widget?region=${this.filterRequest?.regions}&countries=${this.filterRequest?.countries}&period=${this.filterRequest?.period}&widgetType=${widgetType}`;
      url = this.filtersService.appendCustomDateRangeToUrl(url);

      return await this.httpClientService.post(
        url,
        payload
      );
    } catch (error) {
      console.error('Error fetching data for SP Product Widget Data API:', error);
      return axios('fail');
    }
  }

  async getSPTotalSolutionGroupsExpandedData(siteId: string, widgetType: string) {
    try {
      if (this.filterRequest == undefined) {
        throw new Error('filterData is undefined');
      }

      const payload = {
        topLevelContractGroup: this.filterRequest.topLevelContractGroup,
        secondLevelContractFilter: this.filterRequest.secondLevelContractFilter,
        accts: this.filterRequest.accts,
        dataView: this.filterRequest.dataView,
        division: this.filterRequest.division,
        siteId: siteId,
        oneCustomerName: this.oneCustomerName,
        oneCustomerFilters: this.advancedOneCustomerFilters,
        excludeDeletedAccounts: this.filterRequest.excludeDeletedAccounts,
        currencyCode: this.currencyUomService.getCurrency(),
        unitOfMeasure: this.currencyUomService.getUom()
      };

      let url = `${this.ENDPOINT_BASE_PATH}/totalsolutiongroups-expanded/${widgetType}?region=${this.filterRequest?.regions}&countries=${this.filterRequest?.countries}&period=${this.filterRequest?.period}&siteId=${siteId}`;
      url = this.filtersService.appendCustomDateRangeToUrl(url);

      return await this.httpClientService.post(
        url,
        payload
      );
    } catch (error) {
      console.error('Error fetching data for SP Total Solution Groups Expanded Data API:', error);
      return axios('fail');
    }
  }

  //#endregion
}
